/**
 * Árvore do menu, derivada da lista plana de montarMenu (JOR4-7365).
 *
 * Módulo próprio para MenuLateral e FlyoutSubmenu compartilharem o tipo `No`
 * sem import circular.
 */

import type { ItemMenu } from "./montarMenu";

export interface No {
    item: ItemMenu;
    filhos: No[];
}

/**
 * Monta a árvore a partir da lista plana. Um item cujo `categoria` não bate com
 * nenhum ID vira raiz — é o que o dxTreeView fazia, e é o comportamento certo:
 * melhor um item fora do grupo do que um item invisível.
 */
export const montarArvore = (itens: ItemMenu[]): No[] => {
    const porId = new Map<string, No>();
    for (const item of itens) porId.set(String(item.ID), { item, filhos: [] });

    const raizes: No[] = [];
    for (const no of porId.values()) {
        const pai =
            no.item.categoria != null
                ? porId.get(String(no.item.categoria))
                : undefined;
        if (pai) pai.filhos.push(no);
        else raizes.push(no);
    }
    return raizes;
};

/** Um nó é grupo quando tem filhos — ver a nota em MenuLateral. */
export const ehGrupo = (no: No): boolean => no.filhos.length > 0;

export const contemClasseAtiva = (
    no: No,
    classeAtiva: string | null,
): boolean => {
    if (!classeAtiva) return false;
    if (no.item.classInvoke === classeAtiva) return true;
    return no.filhos.some((filho) => contemClasseAtiva(filho, classeAtiva));
};

export interface NoVisivel {
    no: No;
    /** 0 na raiz. Só serve para achar o pai sem carregar um mapa. */
    profundidade: number;
}

/**
 * Nós visíveis na ordem em que aparecem na tela — a lista que as setas ↑/↓
 * percorrem. Filhos de grupo fechado ficam fora, porque navegar por teclado até
 * um item que não está desenhado seria navegar para o nada.
 */
export const achatarVisiveis = (
    arvore: No[],
    estaAberto: (no: No) => boolean,
): NoVisivel[] => {
    const saida: NoVisivel[] = [];

    const visitar = (nos: No[], profundidade: number) => {
        for (const no of nos) {
            saida.push({ no, profundidade });
            if (ehGrupo(no) && estaAberto(no)) {
                visitar(no.filhos, profundidade + 1);
            }
        }
    };

    visitar(arvore, 0);
    return saida;
};

/**
 * Índice do pai de `indice` na lista achatada, ou -1 se ele é raiz. É o primeiro
 * nó acima dele com profundidade menor — o que dispensa guardar o pai no nó.
 */
export const indiceDoPai = (visiveis: NoVisivel[], indice: number): number => {
    const profundidade = visiveis[indice]?.profundidade;
    if (profundidade == null || profundidade === 0) return -1;

    for (let i = indice - 1; i >= 0; i--) {
        if (visiveis[i].profundidade < profundidade) return i;
    }

    return -1;
};
